import { useMemo, useState } from 'react';
import {
  Building2,
  ChevronDown,
  ChevronUp,
  Edit3,
  FileText,
  HelpCircle,
  MapPin,
  Phone,
  Plus,
  Power,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import {
  BoutonMint,
  BoutonSecondaire,
  Chargement,
  Compteur,
  EnteteEcran,
  MessageErreur,
  Pastille,
  MarqueObligatoire,
} from '../components/ui/Primitives';
import { Modale } from '../components/ui/Modale';
import { useToast } from '../components/ui/useToast';
import { ErreurApi } from '../lib/api';
import {
  useAntennesGestion,
  useCreerAntenne,
  useModifierAntenne,
  useBasculerStatutAntenne,
  useSupprimerAntenne,
  useVisaSignatureAntenne,
  type AntenneDetail,
  type FormulaireAntenne,
} from '../lib/useAntennes';
import logoImg from '../assets/img/logo.png';
import { PaveSignature } from '../components/ui/PaveSignature';

const FORMULAIRE_VIERGE: FormulaireAntenne & { code: string } = {
  code: '',
  nom: '',
  adresse: '',
  codePostal: '',
  ville: '',
  telephoneFixe: '',
  telephonePortable: '',
  siret: '',
  uai: '',
  numeroVoie: '',
  voie: '',
  complementAdresse: '',
  cfaDenomination: '',
  cfaUai: '',
  cfaSiret: '',
  cfaNumeroVoie: '',
  cfaVoie: '',
  cfaComplementAdresse: '',
  cfaCodePostal: '',
  cfaCommune: '',
  numeroDeclarationActivite: '',
  cfaEntreprise: false,
  visaResponsableNom: '',
  visaResponsableFonction: '',
};

const CHAMP_TEXTE =
  'min-h-[42px] w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-codialis-navy transition placeholder:text-gray-400 focus:border-codialis-sky focus:outline-none focus:ring-1 focus:ring-codialis-sky';

function SectionForm({ titre, children }: { titre: string; children: React.ReactNode }): JSX.Element {
  return (
    <section className="space-y-3">
      <h3 className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray border-b border-gray-100 pb-1">
        {titre}
      </h3>
      {children}
    </section>
  );
}

function ChampForm({
  libelle,
  aide,
  requis = false,
  children,
}: {
  libelle: string;
  aide?: string;
  requis?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold text-codialis-navy">
        {libelle} {requis && <MarqueObligatoire className="text-red-500" />}
      </span>
      {children}
      {aide && <span className="mt-1 block text-[11px] text-codialis-gray">{aide}</span>}
    </label>
  );
}

export function AdmAntennesPage(): JSX.Element {
  const { data, isLoading, error } = useAntennesGestion();
  const creerMutation = useCreerAntenne();
  const modifierMutation = useModifierAntenne();
  const statutMutation = useBasculerStatutAntenne();
  const supprimerMutation = useSupprimerAntenne();
  const notifier = useToast();

  const [recherche, setRecherche] = useState('');
  const [filtreStatut, setFiltreStatut] = useState<'TOUS' | 'ACTIF' | 'INACTIF'>('TOUS');
  const [apercuAfficheId, setApercuAfficheId] = useState<string | null>(null);
  const [guideOuvert, setGuideOuvert] = useState(false);

  const [modaleOuverte, setModaleOuverte] = useState(false);
  const [antenneEnEdition, setAntenneEnEdition] = useState<AntenneDetail | null>(null);
  const [formulaire, setFormulaire] = useState<FormulaireAntenne & { code: string }>(FORMULAIRE_VIERGE);
  const [erreurFormulaire, setErreurFormulaire] = useState<string | null>(null);
  const visaSignature = useVisaSignatureAntenne(
    antenneEnEdition?.id,
    Boolean(antenneEnEdition?.visaSignatureChemin),
  );

  const antennes = useMemo(() => data?.sites ?? [], [data?.sites]);

  const antennesFiltrees = useMemo(() => {
    return antennes.filter((a) => {
      if (filtreStatut === 'ACTIF' && !a.actif) return false;
      if (filtreStatut === 'INACTIF' && a.actif) return false;
      if (!recherche.trim()) return true;
      const r = recherche.toLowerCase();
      return (
        a.nom.toLowerCase().includes(r) ||
        a.code.toLowerCase().includes(r) ||
        (a.ville && a.ville.toLowerCase().includes(r)) ||
        (a.siret && a.siret.toLowerCase().includes(r))
      );
    });
  }, [antennes, filtreStatut, recherche]);

  // Statistiques
  const actives = useMemo(() => antennes.filter((a) => a.actif), [antennes]);
  const totalApprenants = useMemo(
    () => antennes.reduce((acc, a) => acc + (a._count?.apprenants ?? 0), 0),
    [antennes],
  );
  const totalSessions = useMemo(
    () => antennes.reduce((acc, a) => acc + (a._count?.sessions ?? 0), 0),
    [antennes],
  );

  const ouvrirCreation = () => {
    setAntenneEnEdition(null);
    setFormulaire({
      ...FORMULAIRE_VIERGE,
      numeroDeclarationActivite: '04973592197', // Pré-remplir NDA organisme par défaut
    });
    setErreurFormulaire(null);
    setModaleOuverte(true);
  };

  const ouvrirEdition = (antenne: AntenneDetail) => {
    setAntenneEnEdition(antenne);
    setFormulaire({
      code: antenne.code,
      nom: antenne.nom,
      adresse: antenne.adresse ?? '',
      codePostal: antenne.codePostal ?? '',
      ville: antenne.ville ?? '',
      telephoneFixe: antenne.telephoneFixe ?? '',
      telephonePortable: antenne.telephonePortable ?? '',
      siret: antenne.siret ?? '',
      uai: antenne.uai ?? '',
      numeroVoie: antenne.numeroVoie ?? '',
      voie: antenne.voie ?? antenne.adresse ?? '',
      complementAdresse: antenne.complementAdresse ?? '',
      cfaDenomination: antenne.cfaDenomination ?? '',
      cfaUai: antenne.cfaUai ?? '',
      cfaSiret: antenne.cfaSiret ?? '',
      cfaNumeroVoie: antenne.cfaNumeroVoie ?? '',
      cfaVoie: antenne.cfaVoie ?? '',
      cfaComplementAdresse: antenne.cfaComplementAdresse ?? '',
      cfaCodePostal: antenne.cfaCodePostal ?? '',
      cfaCommune: antenne.cfaCommune ?? '',
      numeroDeclarationActivite: antenne.numeroDeclarationActivite ?? '04973592197',
      cfaEntreprise: antenne.cfaEntreprise,
      visaResponsableNom: antenne.visaResponsableNom ?? '',
      visaResponsableFonction: antenne.visaResponsableFonction ?? '',
    });
    setErreurFormulaire(null);
    setModaleOuverte(true);
  };

  const enregistrer = async (e: React.FormEvent) => {
    e.preventDefault();
    setErreurFormulaire(null);

    if (!formulaire.nom.trim()) {
      setErreurFormulaire('Le nom de l’antenne est obligatoire.');
      return;
    }

    if (!antenneEnEdition && !formulaire.code.trim()) {
      setErreurFormulaire('Le code antenne est obligatoire.');
      return;
    }

    try {
      if (antenneEnEdition) {
        await modifierMutation.mutateAsync({
          id: antenneEnEdition.id,
          donnees: formulaire,
        });
        notifier(`L’antenne ${formulaire.nom} a été modifiée avec succès.`);
      } else {
        await creerMutation.mutateAsync(formulaire);
        notifier(`L’antenne ${formulaire.nom} a été créée avec succès.`);
      }
      setModaleOuverte(false);
    } catch (err) {
      if (err instanceof ErreurApi) {
        setErreurFormulaire(err.message);
      } else {
        setErreurFormulaire('Une erreur inattendue est survenue.');
      }
    }
  };

  const basculerStatut = async (antenne: AntenneDetail) => {
    try {
      await statutMutation.mutateAsync({
        id: antenne.id,
        actif: !antenne.actif,
      });
      notifier(`L’antenne ${antenne.nom} est maintenant ${!antenne.actif ? 'active' : 'inactive'}.`);
    } catch (err) {
      if (err instanceof ErreurApi) {
        notifier(err.message);
      } else {
        notifier('Erreur lors du changement de statut.');
      }
    }
  };

  const supprimer = async (antenne: AntenneDetail) => {
    if (!window.confirm(`Voulez-vous vraiment supprimer l’antenne « ${antenne.nom} » ?`)) {
      return;
    }
    try {
      await supprimerMutation.mutateAsync(antenne.id);
      notifier(`L’antenne ${antenne.nom} a été supprimée.`);
    } catch (err) {
      if (err instanceof ErreurApi) {
        notifier(err.message);
      } else {
        notifier('Erreur lors de la suppression.');
      }
    }
  };

  return (
    <div className="mx-auto max-w-[1180px] space-y-6 p-4 md:p-6">
      {/* En-tête de la page */}
      <EnteteEcran
        titre="Antennes & Coordonnées documentaires"
        sousTitre="Gestion des sites et coordonnées officielles imprimées sur les documents PDF (contrats, conventions, attestations)."
        actions={
          <BoutonMint onClick={ouvrirCreation}>
            <span className="inline-flex items-center gap-2">
              <Plus className="h-4 w-4" aria-hidden="true" />
              Nouvelle antenne
            </span>
          </BoutonMint>
        }
      />

      {/* Cartes statistiques */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Compteur
          libelle="Antennes actives"
          valeur={actives.length}
          detail="Sites opérationnels"
          ton="mint"
        />
        <Compteur
          libelle="Total référencées"
          valeur={antennes.length}
          detail="Pôles enregistrés"
          ton="navy"
        />
        <Compteur
          libelle="Apprenants rattachés"
          valeur={totalApprenants}
          detail="Sur l'ensemble des sites"
          ton="blue"
        />
        <Compteur
          libelle="Sessions de formation"
          valeur={totalSessions}
          detail="Sessions actives"
          ton="warm"
        />
      </div>

      {/* Bannière d'aide & contexte réglementaire */}
      <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm transition hover:border-gray-300">
        <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:gap-4">
          <div className="flex items-start gap-3">
            <div className="rounded-xl bg-codialis-blue/10 p-2 text-codialis-blue shrink-0">
              <HelpCircle className="h-5 w-5" />
            </div>
            <div className="space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-sm font-bold text-codialis-navy">
                  Personnalisation dynamique des documents officiels
                </h2>
                <span className="rounded bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-600">
                  Qualiopi & DREETS
                </span>
              </div>
              <p className="text-xs text-codialis-gray leading-relaxed">
                Les coordonnées saisies pour chaque antenne (Adresse, SIRET, Téléphones et N° de Déclaration d'Activité) sont injectées en temps réel dans l'en-tête et le pied de page des documents générés.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setGuideOuvert(!guideOuvert)}
            className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-codialis-sky hover:underline sm:pt-1"
          >
            {guideOuvert ? 'Masquer' : 'En savoir plus'}
            {guideOuvert ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </button>
        </div>

        {guideOuvert && (
          <div className="mt-3.5 rounded-xl border border-blue-100 bg-blue-50/50 p-3.5 text-xs text-codialis-navy space-y-2">
            <p>
              <strong>À quoi correspond le N° 04 97 35 921 97 ?</strong> C'est le <strong>Numéro de Déclaration d'Activité (NDA)</strong> délivré par la DREETS (Préfecture de La Réunion) qui autorise TOP FORMATION à exercer en tant qu'organisme de formation professionnelle.
            </p>
            <p className="text-codialis-gray">
              Conformément à l'article R6351-6 du Code du Travail, ce numéro ainsi que le SIRET et le siège/antenne doivent figurer sur chaque bon de commande, convention, devis et attestation de formation.
            </p>
          </div>
        )}
      </div>

      {/* Barre d'actions & Filtres */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        {/* Barre de recherche */}
        <div className="relative min-w-0 flex-1 max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            aria-label="Rechercher une antenne"
            value={recherche}
            onChange={(e) => setRecherche(e.target.value)}
            placeholder="Rechercher une antenne, ville, code..."
            className="min-h-[38px] [@media(pointer:coarse)]:min-h-[40px] w-full rounded-xl border border-gray-200 bg-white pl-9 pr-3 text-xs text-codialis-navy placeholder:text-gray-400 focus:border-codialis-sky focus:outline-none"
          />
          {recherche && (
            <button
              type="button"
              onClick={() => setRecherche('')}
              aria-label="Effacer la recherche"
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Filtres statut */}
        <div className="flex items-center gap-1.5 self-end sm:self-auto">
          {(['TOUS', 'ACTIF', 'INACTIF'] as const).map((statut) => (
            <button
              key={statut}
              type="button"
              onClick={() => setFiltreStatut(statut)}
              aria-pressed={filtreStatut === statut}
              className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                filtreStatut === statut
                  ? 'bg-codialis-navy text-white shadow-sm'
                  : 'bg-white border border-gray-200 text-codialis-gray hover:bg-gray-50'
              }`}
            >
              {statut === 'TOUS' ? 'Toutes' : statut === 'ACTIF' ? 'Actives' : 'Inactives'}
            </button>
          ))}
        </div>
      </div>

      {/* Liste des antennes */}
      {error && <MessageErreur message="Impossible de charger la liste des antennes." />}
      {isLoading && <Chargement libelle="Chargement des antennes…" />}

      {!isLoading && !error && antennesFiltrees.length === 0 && (
        <div className="rounded-2xl border border-dashed border-gray-300 bg-white py-12 text-center shadow-sm">
          <Building2 className="mx-auto h-10 w-10 text-gray-300" />
          <p className="mt-2 text-sm font-medium text-codialis-navy">
            {recherche ? 'Aucune antenne ne correspond à votre recherche.' : 'Aucune antenne configurée.'}
          </p>
          {recherche && (
            <button
              type="button"
              onClick={() => setRecherche('')}
              className="mt-2 text-xs font-semibold text-codialis-sky hover:underline"
            >
              Réinitialiser le filtre
            </button>
          )}
        </div>
      )}

      {!isLoading && !error && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {antennesFiltrees.map((antenne) => {
            const telCombine =
              [antenne.telephoneFixe, antenne.telephonePortable].filter(Boolean).join(' · ') ||
              antenne.telephone ||
              'Non renseigné';
            const adresseComplete =
              [antenne.adresse, antenne.codePostal, antenne.ville].filter(Boolean).join(', ') ||
              'Non renseignée';
            const estApercuOuvert = apercuAfficheId === antenne.id;

            return (
              <div
                key={antenne.id}
                className={`flex flex-col justify-between rounded-2xl border bg-white p-4 transition-all shadow-sm hover:shadow-md ${
                  antenne.actif ? 'border-gray-200 hover:border-gray-300' : 'border-gray-200 bg-gray-50/60 opacity-80'
                }`}
              >
                <div className="space-y-3.5">
                  {/* Haut de carte : Code, Nom, Statut & Actions directes */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2.5">
                      <span className="rounded-lg bg-codialis-navy px-2.5 py-1 font-mono text-xs font-bold text-white shadow-sm">
                        {antenne.code}
                      </span>
                      <div>
                        <h2 className="font-display text-base font-bold text-codialis-navy leading-tight">
                          {antenne.nom}
                        </h2>
                        <p className="text-xs text-codialis-gray">
                          {antenne.ville ? `Site de ${antenne.ville}` : 'Localisation à définir'}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5">
                      <Pastille
                        texte={antenne.actif ? 'Active' : 'Inactive'}
                        ton={antenne.actif ? 'success' : 'neutral'}
                      />
                      <button
                        aria-label={antenne.actif ? 'Désactiver l’antenne' : 'Activer l’antenne'}
                        type="button"
                        onClick={() => basculerStatut(antenne)}
                        className={`rounded-lg p-1.5 transition ${
                          antenne.actif
                            ? 'text-gray-400 hover:bg-red-50 hover:text-red-600'
                            : 'text-codialis-teal hover:bg-emerald-50'
                        }`}
                        title={antenne.actif ? 'Désactiver l’antenne' : 'Activer l’antenne'}
                      >
                        <Power className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {/* Coordonnées compactes */}
                  <div className="grid grid-cols-1 gap-2 rounded-xl bg-gray-50/80 p-3 text-xs text-codialis-navy border border-gray-100">
                    <div className="flex items-start gap-2">
                      <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-codialis-gray" />
                      <span className="truncate text-gray-700 font-medium" title={adresseComplete}>
                        {adresseComplete}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <Phone className="h-3.5 w-3.5 shrink-0 text-codialis-gray" />
                      <span className="text-gray-700">{telCombine}</span>
                    </div>

                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-1 border-t border-gray-200/60 text-[11px]">
                      <div>
                        <span className="text-gray-400 font-medium">SIRET : </span>
                        <span className="font-mono font-semibold text-gray-800">
                          {antenne.siret || '—'}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-400 font-medium">NDA : </span>
                        <span className="font-mono font-semibold text-gray-800">
                          {antenne.numeroDeclarationActivite || '—'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Accordéon Aperçu du pied de page PDF */}
                  <div>
                    <button
                      type="button"
                      onClick={() => setApercuAfficheId(estApercuOuvert ? null : antenne.id)}
                      className="inline-flex w-full items-center justify-between rounded-lg border border-dashed border-gray-200 bg-gray-50/50 px-2.5 py-1.5 text-left text-[11px] font-medium text-codialis-gray transition hover:bg-gray-100 hover:text-codialis-navy"
                    >
                      <span className="flex items-center gap-1.5">
                        <FileText className="h-3.5 w-3.5 text-codialis-sky" />
                        <span>Aperçu du pied de page PDF</span>
                      </span>
                      {estApercuOuvert ? (
                        <ChevronUp className="h-3.5 w-3.5" />
                      ) : (
                        <ChevronDown className="h-3.5 w-3.5" />
                      )}
                    </button>

                    {estApercuOuvert && (
                      <div className="mt-2 rounded-xl border border-gray-200 bg-white p-3 shadow-inner text-[10px] text-gray-600">
                        <div className="flex items-center justify-between border-b border-gray-200 pb-1 text-[9px] text-gray-400">
                          <span>Document officiel · Pied de page</span>
                          <span className="font-bold text-codialis-navy flex items-center gap-1">
                            TOP FORMATION
                            <img src={logoImg} alt="Logo" className="h-2.5 w-auto object-contain inline" />
                          </span>
                          <span>Page 1/1</span>
                        </div>
                        <div className="pt-2 text-center text-[9px] leading-tight space-y-0.5">
                          <div className="font-semibold text-gray-700">
                            TOP FORMATION – SIRET : {antenne.siret || '932 379 472 00024'} – N° {antenne.numeroDeclarationActivite || '04973592197'}
                          </div>
                          <div className="text-gray-500">
                            Siège social : {adresseComplete}
                          </div>
                          <div className="text-gray-500">
                            Tél : {telCombine}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Bas de carte : Rattachements & Boutons d'action */}
                <div className="mt-4 flex items-center justify-between border-t border-gray-100 pt-3 text-xs">
                  <div className="flex items-center gap-2 text-[11px] text-codialis-gray">
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 font-medium">
                      {antenne._count?.apprenants ?? 0} apprenant(s)
                    </span>
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 font-medium">
                      {antenne._count?.sessions ?? 0} session(s)
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => ouvrirEdition(antenne)}
                      className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2.5 py-1 text-xs font-semibold text-codialis-navy shadow-sm transition hover:bg-gray-50"
                    >
                      <Edit3 className="h-3.5 w-3.5 text-gray-500" />
                      Modifier
                    </button>

                    {antenne._count &&
                      antenne._count.apprenants === 0 &&
                      antenne._count.sessions === 0 &&
                      antenne._count.utilisateurs === 0 && (
                        <button
                          aria-label="Supprimer cette antenne non rattachée"
                          type="button"
                          onClick={() => supprimer(antenne)}
                          className="rounded-lg p-1 text-gray-400 transition hover:bg-red-50 hover:text-red-600"
                          title="Supprimer cette antenne non rattachée"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modale de création / modification épurée et structurée */}
      <Modale
        titre={antenneEnEdition ? `Modifier ${antenneEnEdition.nom}` : 'Ajouter une antenne'}
        sousTitre="Renseignez les coordonnées qui figureront sur tous les documents officiels générés."
        ouverte={modaleOuverte}
        onFermer={() => setModaleOuverte(false)}
        largeur="max-w-2xl"
      >
        <form onSubmit={enregistrer} className="space-y-5 px-4 sm:px-6 py-5">
          {erreurFormulaire && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-700">
              {erreurFormulaire}
            </div>
          )}

          {/* Section 1 : Identité */}
          <SectionForm titre="Identité du site">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <ChampForm
                libelle="Code antenne"
                aide={antenneEnEdition ? 'Identifiant unique figé' : 'Court, en majuscules (ex: NORD, SUD, EST)'}
                requis
              >
                <input
                  type="text"
                  value={formulaire.code}
                  onChange={(e) => setFormulaire({ ...formulaire, code: e.target.value.toUpperCase() })}
                  disabled={Boolean(antenneEnEdition)}
                  placeholder="Ex: NORD"
                  required
                  className={`${CHAMP_TEXTE} font-mono uppercase disabled:bg-gray-100 disabled:text-gray-400`}
                />
              </ChampForm>

              <ChampForm libelle="Nom de l'antenne" requis>
                <input
                  type="text"
                  value={formulaire.nom}
                  onChange={(e) => setFormulaire({ ...formulaire, nom: e.target.value })}
                  placeholder="Ex: Antenne NORD - Saint-Denis"
                  required
                  className={CHAMP_TEXTE}
                />
              </ChampForm>
            </div>
          </SectionForm>

          {/* Section 2 : Informations générales */}
          <SectionForm titre="Informations générales">
            <div className="space-y-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <ChampForm libelle="UAI">
                  <input
                    type="text"
                    value={formulaire.uai}
                    onChange={(e) => setFormulaire({ ...formulaire, uai: e.target.value })}
                    className={`${CHAMP_TEXTE} font-mono`}
                  />
                </ChampForm>
                <ChampForm libelle="SIRET" aide="14 chiffres de l’établissement">
                  <input
                    type="text"
                    value={formulaire.siret}
                    onChange={(e) => setFormulaire({ ...formulaire, siret: e.target.value })}
                    className={`${CHAMP_TEXTE} font-mono`}
                  />
                </ChampForm>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <ChampForm libelle="N° de voie">
                  <input
                    type="text"
                    value={formulaire.numeroVoie}
                    onChange={(e) => setFormulaire({ ...formulaire, numeroVoie: e.target.value })}
                    className={CHAMP_TEXTE}
                  />
                </ChampForm>
                <div className="sm:col-span-2 min-w-0">
                  <ChampForm libelle="Voie">
                    <input
                      type="text"
                      value={formulaire.voie}
                      onChange={(e) => setFormulaire({ ...formulaire, voie: e.target.value, adresse: e.target.value })}
                      placeholder="Ex : rue Paul Demange"
                      className={CHAMP_TEXTE}
                    />
                  </ChampForm>
                </div>
              </div>
              <ChampForm libelle="Complément">
                <input
                  type="text"
                  value={formulaire.complementAdresse}
                  onChange={(e) => setFormulaire({ ...formulaire, complementAdresse: e.target.value })}
                  placeholder="Bâtiment, étage, boîte postale…"
                  className={CHAMP_TEXTE}
                />
              </ChampForm>
              <ChampForm libelle="Adresse complète (documents existants)">
                <input
                  type="text"
                  value={formulaire.adresse}
                  onChange={(e) => setFormulaire({ ...formulaire, adresse: e.target.value, voie: e.target.value })}
                  placeholder="Ex: 7 rue Paul Demange"
                  className={CHAMP_TEXTE}
                />
              </ChampForm>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <ChampForm libelle="Code postal">
                  <input
                    type="text"
                    value={formulaire.codePostal}
                    onChange={(e) => setFormulaire({ ...formulaire, codePostal: e.target.value })}
                    placeholder="Ex: 97480"
                    className={CHAMP_TEXTE}
                  />
                </ChampForm>

                <ChampForm libelle="Ville">
                  <input
                    type="text"
                    value={formulaire.ville}
                    onChange={(e) => setFormulaire({ ...formulaire, ville: e.target.value })}
                    placeholder="Ex: Saint-Joseph"
                    className={CHAMP_TEXTE}
                  />
                </ChampForm>
              </div>
            </div>
          </SectionForm>

          {/* Section 3 : Téléphonie */}
          <SectionForm titre="Téléphonie">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <ChampForm libelle="Téléphone fixe">
                <input
                  type="text"
                  value={formulaire.telephoneFixe}
                  onChange={(e) => setFormulaire({ ...formulaire, telephoneFixe: e.target.value })}
                  placeholder="Ex: 0262 23 86 17"
                  className={CHAMP_TEXTE}
                />
              </ChampForm>

              <ChampForm libelle="Téléphone mobile / portable">
                <input
                  type="text"
                  value={formulaire.telephonePortable}
                  onChange={(e) => setFormulaire({ ...formulaire, telephonePortable: e.target.value })}
                  placeholder="Ex: 0692 44 54 46"
                  className={CHAMP_TEXTE}
                />
              </ChampForm>
            </div>
          </SectionForm>

          {/* Section 4 : Mentions légales officielles */}
          <SectionForm titre="Mentions légales (Documents officiels)">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <ChampForm libelle="N° Déclaration d’activité" aide="Attestation préfectorale DREETS">
                <input
                  type="text"
                  value={formulaire.numeroDeclarationActivite}
                  onChange={(e) =>
                    setFormulaire({ ...formulaire, numeroDeclarationActivite: e.target.value })
                  }
                  placeholder="Ex: 04973592197"
                  className={`${CHAMP_TEXTE} font-mono`}
                />
              </ChampForm>
            </div>
          </SectionForm>

          <SectionForm titre="CFA responsable">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <ChampForm libelle="Dénomination du CFA">
                <input value={formulaire.cfaDenomination} onChange={(e) => setFormulaire({ ...formulaire, cfaDenomination: e.target.value })} className={CHAMP_TEXTE} />
              </ChampForm>
              <ChampForm libelle="N° UAI du CFA">
                <input value={formulaire.cfaUai} onChange={(e) => setFormulaire({ ...formulaire, cfaUai: e.target.value })} className={`${CHAMP_TEXTE} font-mono`} />
              </ChampForm>
              <ChampForm libelle="N° SIRET du CFA">
                <input value={formulaire.cfaSiret} onChange={(e) => setFormulaire({ ...formulaire, cfaSiret: e.target.value })} className={`${CHAMP_TEXTE} font-mono`} />
              </ChampForm>
              <ChampForm libelle="N° de voie du CFA">
                <input value={formulaire.cfaNumeroVoie} onChange={(e) => setFormulaire({ ...formulaire, cfaNumeroVoie: e.target.value })} className={CHAMP_TEXTE} />
              </ChampForm>
              <ChampForm libelle="Voie du CFA">
                <input value={formulaire.cfaVoie} onChange={(e) => setFormulaire({ ...formulaire, cfaVoie: e.target.value })} className={CHAMP_TEXTE} />
              </ChampForm>
              <ChampForm libelle="Complément d’adresse du CFA">
                <input value={formulaire.cfaComplementAdresse} onChange={(e) => setFormulaire({ ...formulaire, cfaComplementAdresse: e.target.value })} className={CHAMP_TEXTE} />
              </ChampForm>
              <ChampForm libelle="Code postal du CFA">
                <input value={formulaire.cfaCodePostal} onChange={(e) => setFormulaire({ ...formulaire, cfaCodePostal: e.target.value })} className={CHAMP_TEXTE} />
              </ChampForm>
              <ChampForm libelle="Commune du CFA">
                <input value={formulaire.cfaCommune} onChange={(e) => setFormulaire({ ...formulaire, cfaCommune: e.target.value })} className={CHAMP_TEXTE} />
              </ChampForm>
            </div>
          </SectionForm>

          <SectionForm titre="Visa CFA pour les contrats d’apprentissage">
            <div className="space-y-3">
              <label className="flex items-start gap-3 rounded-xl border border-gray-200 bg-gray-50/60 p-3 text-sm text-codialis-navy">
                <input
                  type="checkbox"
                  checked={Boolean(formulaire.cfaEntreprise)}
                  onChange={(e) => setFormulaire({ ...formulaire, cfaEntreprise: e.target.checked })}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-codialis-sky focus:ring-codialis-sky"
                />
                <span>
                  <span className="block font-semibold">Le CFA responsable est-il un CFA d’entreprise ?</span>
                  <span className="block text-xs text-codialis-gray">Cette réponse est reprise automatiquement dans chaque CERFA de l’antenne.</span>
                </span>
              </label>

              {antenneEnEdition ? (
                <>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <ChampForm libelle="Responsable signataire du CFA">
                      <input
                        type="text"
                        value={formulaire.visaResponsableNom ?? ''}
                        onChange={(e) => setFormulaire({ ...formulaire, visaResponsableNom: e.target.value })}
                        placeholder="Prénom NOM"
                        className={CHAMP_TEXTE}
                      />
                    </ChampForm>
                    <ChampForm libelle="Fonction">
                      <input
                        type="text"
                        value={formulaire.visaResponsableFonction ?? ''}
                        onChange={(e) => setFormulaire({ ...formulaire, visaResponsableFonction: e.target.value })}
                        placeholder="Directeur·rice / responsable CFA"
                        className={CHAMP_TEXTE}
                      />
                    </ChampForm>
                  </div>
                  <ChampForm
                    libelle="Signature du responsable CFA"
                    aide={antenneEnEdition.visaSignatureChemin ? 'Une signature est déjà enregistrée. Dessinez une nouvelle signature pour la remplacer.' : 'Cette signature sera conservée dans le stockage privé de l’antenne.'}
                  >
                    <PaveSignature
                      key={antenneEnEdition.id}
                      traceInitiale={visaSignature.data?.trace ?? null}
                      onChanger={(trace) =>
                        setFormulaire((precedent) => ({ ...precedent, visaSignature: trace }))
                      }
                    />
                  </ChampForm>
                  <ChampForm
                    libelle="Cachet du CFA"
                    aide={antenneEnEdition.visaCachetChemin ? 'Un cachet est déjà enregistré. Sélectionnez une image pour le remplacer.' : 'PNG ou JPEG, conservé dans le stockage privé de l’antenne.'}
                  >
                    <input
                      type="file"
                      accept="image/png,image/jpeg"
                      className={`${CHAMP_TEXTE} py-2`}
                      onChange={(e) => {
                        const fichier = e.target.files?.[0];
                        if (!fichier) return;
                        if (fichier.size > 2 * 1024 * 1024) {
                          setErreurFormulaire('Le cachet CFA ne peut pas dépasser 2 Mo.');
                          return;
                        }
                        const lecteur = new FileReader();
                        lecteur.onload = () => {
                          const resultat = lecteur.result;
                          if (typeof resultat === 'string') {
                            setFormulaire((precedent) => ({ ...precedent, visaCachet: resultat }));
                          }
                        };
                        lecteur.readAsDataURL(fichier);
                      }}
                    />
                  </ChampForm>
                </>
              ) : (
                <p className="text-xs text-codialis-gray">Créez d’abord l’antenne, puis ouvrez-la en modification pour enregistrer le visa et sa signature durable.</p>
              )}
            </div>
          </SectionForm>

          {/* Aperçu en direct dans la modale */}
          <div className="rounded-xl border border-gray-200 bg-gray-50/70 p-3 text-[11px] text-codialis-gray">
            <div className="flex items-center justify-between font-semibold text-codialis-navy mb-1">
              <span>Aperçu dynamique du pied de page :</span>
              <span className="text-[10px] text-codialis-teal">Temps réel</span>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-2.5 text-center text-[10px] text-gray-700 leading-tight space-y-0.5">
              <div className="font-semibold text-codialis-navy">
                TOP FORMATION – SIRET : {formulaire.siret || '932 379 472 XXXXX'} – N° {formulaire.numeroDeclarationActivite || '04973592197'}
              </div>
              <div className="text-gray-500">
                Siège social : {[formulaire.adresse, formulaire.codePostal, formulaire.ville].filter(Boolean).join(', ') || 'Adresse non renseignée'}
              </div>
              <div className="text-gray-500">
                Tél : {[formulaire.telephoneFixe, formulaire.telephonePortable].filter(Boolean).join(' · ') || 'Téléphone non renseigné'}
              </div>
            </div>
          </div>

          {/* Boutons d'action */}
          <div className="flex items-center justify-end gap-3 border-t border-gray-100 pt-4">
            <BoutonSecondaire onClick={() => setModaleOuverte(false)}>
              Annuler
            </BoutonSecondaire>
            <BoutonMint
              type="submit"
              disabled={creerMutation.isPending || modifierMutation.isPending}
            >
              {creerMutation.isPending || modifierMutation.isPending
                ? 'Enregistrement…'
                : antenneEnEdition
                ? 'Enregistrer les modifications'
                : 'Créer l’antenne'}
            </BoutonMint>
          </div>
        </form>
      </Modale>
    </div>
  );
}
